import React, { useEffect, useState, useMemo } from 'react';
import api from '../../libs/axios';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from '../../utils/pagination';
import Breadcrumb from '../../utils/breadcrumbs';
import formatDate from '../../utils/format-date';
import { FiEye, FiTrash2, FiPlus } from 'react-icons/fi';
import ConfirmationModal from '../../ui/confirmation-modal';
import toast from 'react-hot-toast';
import { ListLoader } from '../../utils/loader';

export enum MatchType {
    FRIENDLY = "friendly",
    TOURNAMENT = "tournament",
    PRACTICE = "practice",
    LEAGUE = "league",
}

export enum MatchStatus {
    CREATED = "created",
    PLAYERS_SELECTED = "players_selected",
    SCHEDULED = "scheduled",
    TOSS_PENDING = "toss_pending",
    LINEUP_PENDING = "lineup_pending",
    LIVE = "live",
    FIRST_INNINGS = "first_innings",
    INNINGS_BREAK = "innings_break",
    SECOND_INNINGS = "second_innings",
    COMPLETED = "completed",
    ABANDONED = "abandoned",
    CANCELLED = "cancelled",
}

export interface IMatch {
    id: number;
    title: string;
    match_type: MatchType;
    match_date: string;
    status: MatchStatus;
    total_overs: number;
    city: string;
    setup_step: string;
    toss_winner_team_id: number | null;
    toss_decision: string | null;
    winning_team_id: number | null;
    result_summary: string | null;
    created_at: string;
    teamA: {
        id: number;
        name: string;
        logo_url: string;
    } | null;
    teamB: {
        id: number;
        name: string;
        logo_url: string;
    } | null;
    tossWinner: {
        id: number;
        name: string;
    } | null;
    winner: {
        id: number;
        name: string;
    } | null;
    ground: {
        id: number;
        name: string;
    };
    // Null when the organiser never completed a profile (no user_details row).
    organizer: {
        id: number;
        details: {
            first_name: string;
            last_name: string;
        } | null;
    } | null;
    tournament: any;
}

// One organiser without a profile used to throw here and blank the whole page.
const organizerName = (match: IMatch): string => {
    const d = match.organizer?.details;
    const name = [d?.first_name, d?.last_name].filter(Boolean).join(' ').trim();
    if (name) return name;
    return match.organizer?.id ? `User #${match.organizer.id} (no profile)` : 'Unknown';
};

const Matches: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useState<Record<string, string>>({});
    const [selectedMatch, setSelectedMatch] = useState<IMatch | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [matches, setMatches] = useState<IMatch[]>([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    });
    const [loading, setLoading] = useState(false);

    // Updated getTeamNames function to use correct API structure
    const getTeamNames = (match: IMatch) => {
        if (match.teamA && match.teamB) {
            return `${match.teamA.name} vs ${match.teamB.name}`;
        } else if (match.teamA) {
            return `${match.teamA.name} vs TBD`;
        } else if (match.teamB) {
            return `TBD vs ${match.teamB.name}`;
        } else {
            return 'Teams not assigned';
        }
    };

    // Filter matches based on search parameters (client-side filtering)
    const filteredMatches = useMemo(() => {
        return matches.filter((match) => {
            const titleMatch = !searchParams.title ||
                match.title.toLowerCase().includes(searchParams.title.toLowerCase());

            const teamsMatch = !searchParams.teams ||
                getTeamNames(match).toLowerCase().includes(searchParams.teams.toLowerCase());

            const statusMatch = !searchParams.status ||
                match.status.toLowerCase() === searchParams.status.toLowerCase();

            return titleMatch && teamsMatch && statusMatch;
        });
    }, [matches, searchParams]);

    // Fetch matches from API
    const fetchMatches = async (useSearchParams = false) => {
        setLoading(true);
        try {
            let url = `/matches?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`;

            // Add search parameters to API call if they exist and useSearchParams is true
            if (useSearchParams && Object.keys(searchParams).length > 0) {
                const params = new URLSearchParams();
                if (searchParams.title) params.append('search', searchParams.title);
                if (searchParams.status) params.append('status', searchParams.status);
                url += `&${params.toString()}`;
            }

            const response = await api.get(url);
            setMatches(response.data.matches);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching matches:', error);
            setMatches([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch matches on component mount and when dependencies change
    useEffect(() => {
        fetchMatches();
    }, [pagination.currentPage, pagination.itemsPerPage, searchParams?.status]);

    // Search in database when filtered list is empty but search params exist
    useEffect(() => {
        if (Object.keys(searchParams).length > 0 && filteredMatches.length === 0 && !loading) {
            fetchMatches(true);
        }
    }, [filteredMatches.length, searchParams]);

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = { ...prev };
            if (value) {
                newParams[column] = value;
            } else {
                delete newParams[column];
            }
            return newParams;
        });
        setPagination(prev => ({ ...prev, currentPage: 1 }));

        // If search is cleared (empty value), fetch fresh data from API
        if (!value) {
            fetchMatches(false);
        }
    };

    // Get status style
    const getStatusStyle = (status: MatchStatus) => {
        switch (status.toLowerCase()) {
            case 'live': return 'bg-red-100 text-red-800';
            case 'completed': return 'bg-green-100 text-green-800';
            case 'scheduled': return 'bg-blue-100 text-blue-800';
            case 'created':
            case 'players_selected':
            case 'toss_pending':
            case 'lineup_pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
            case 'abandoned':
                return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setPagination(prev => ({
            ...prev,
            itemsPerPage: newLimit,
            currentPage: 1
        }));
    };

    // Handle row click to navigate to view page
    const handleRowClick = (match: IMatch, event: React.MouseEvent) => {
        // Prevent navigation if clicking on action buttons
        if ((event.target as HTMLElement).closest('button') || (event.target as HTMLElement).closest('a')) {
            return;
        }
        navigate('/matches/view', { state: { matchId: match.id } });
    };

    // Handle delete
    const handleDeleteClick = (match: IMatch, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click
        setSelectedMatch(match);
        setDeleteConfirmOpen(true);
    };

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedMatch) {
            try {
                setLoading(true);
                await api.delete(`/matches/${selectedMatch.id}`);

                // Refresh the matches list
                await fetchMatches();
                toast.success(`${selectedMatch.title} deleted successfully!`);
            } catch (err) {
                console.error('Error deleting match:', err);
                toast.error('Failed to delete match. Please try again.');
            } finally {
                setLoading(false);
                setDeleteConfirmOpen(false);
                setSelectedMatch(null);
            }
        }
    };

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Matches
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <Link
                        to="/matches/create"
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiPlus className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add Match</span>
                    </Link>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Match Details
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Teams
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Venue
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Title"
                                        value={searchParams.title || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('title', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Teams"
                                        value={searchParams.teams || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('teams', e.target.value)}
                                    />
                                </td>

                                <td className="px-6 py-2 border-r"></td>

                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('status', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="created">Created</option>
                                        <option value="scheduled">Scheduled</option>
                                        <option value="live">Live</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredMatches.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-8 text-gray-500">
                                        No matches found
                                    </td>
                                </tr>
                            ) : (
                                filteredMatches.map(match => (
                                    <tr
                                        key={match.id}
                                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={(e) => handleRowClick(match, e)}
                                    >
                                        <td className="px-4 py-3 border-r">
                                            <div className="space-y-1">
                                                {/* Title & Match Type */}
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium capitalize text-gray-900">{match.title}</span>
                                                    <span className="bg-blue-100 capitalize text-primary-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                                        {match.match_type}
                                                    </span>
                                                </div>

                                                {/* Organizer */}
                                                <div className="text-sm text-gray-600">
                                                    Organizer: {organizerName(match)}
                                                </div>

                                                {/* Date */}
                                                <div className="text-sm text-gray-600">
                                                    {formatDate(match.match_date)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-r">
                                            <div className="font-medium text-gray-900 mt-1">
                                                {getTeamNames(match)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-r">
                                            <div className="space-y-1">
                                                {/* Venue */}
                                                <div className="font-medium text-gray-900">
                                                    {match.ground.name}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {match.city}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-r">
                                            <span className={`px-2 capitalize py-1 text-xs rounded-full ${getStatusStyle(match.status)}`}>
                                                {match.status.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3">
                                                <Link
                                                    to={`/matches/view`}
                                                    state={{ matchId: match.id }}
                                                    className="text-primary-600 hover:text-primary-700"
                                                    title="View Match"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </Link>
                                                <button
                                                    onClick={(e) => handleDeleteClick(match, e)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete Match"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Match"
                        description={`Are you sure you want to delete "${selectedMatch?.title}"? This action cannot be undone.`}
                        loading={loading}
                    />
                )}
            </div>
        </div>
    );
};

export default Matches;